import { useMemo, useState, useContext } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { ToastContext } from 'contexts';
import { VENDORS_ME_SUBMISSIONS } from 'constants/apis';
import { useApp } from 'providers/AppProvider';
import axios from 'helpers/axios';

const guessedTz = moment.tz.guess();

const isWipSubmission = ({ completeSubmission, dateClose }, timezone) => {
  const closingDate = moment.utc(dateClose).tz(timezone);
  const now = moment.tz(timezone);
  return !completeSubmission && closingDate.isAfter(now);
};

const isCompletedSubmission = ({ completeSubmission }) => completeSubmission;

const isAwardedSubmission = ({ completeSubmission, awardedProposalId }) =>
  completeSubmission && awardedProposalId;

const isMissedSubmission = ({ completeSubmission, dateClose }, timezone) => {
  const closingDate = moment.utc(dateClose).tz(timezone);
  const now = moment.tz(timezone);

  // A null `dateClose` indicates the project was cancelled.
  return !completeSubmission && (!dateClose || closingDate.isBefore(now));
};

export const useDashboardSubmissions = () => {
  const { apiRegions, timezone } = useApp();
  const { t } = useTranslation();
  const { triggerToast } = useContext(ToastContext);
  const [hasShownToast, setHasShownToast] = useState(false);

  const submissionResults = useQueries({
    queries: apiRegions.map(region => ({
      queryKey: ['vendors', 'me', 'submissions', region],
      queryFn: () =>
        axios.get(VENDORS_ME_SUBMISSIONS, {
          params: { region },
        }),
      onError: () => {
        // Only show a single toast even if all region queries fail.
        // The component will have to remount anyways to refetch the query.
        if (!hasShownToast) {
          triggerToast({ message: t('generic_error') });
          setHasShownToast(true);
        }
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  const submissions = useMemo(
    () =>
      submissionResults.reduce(
        (submissions, result) => {
          const { data: { data } = {}, isError, isLoading } = result;

          if (isError || isLoading || !data?.length) {
            return submissions;
          }

          for (const submission of data) {
            // Kindling `DataTable` component expects an `id` field
            // to be defined in order to ensure stability.
            submission.id = submission.projectId;

            if (isWipSubmission(submission, timezone ?? guessedTz)) {
              submissions.wip.push(submission);
            }
            if (isCompletedSubmission(submission)) {
              submissions.submitted.push(submission);
            }
            if (isAwardedSubmission(submission)) {
              submissions.awarded.push(submission);
            }
            if (isMissedSubmission(submission, timezone ?? guessedTz)) {
              submissions.missed.push(submission);
            }
          }

          return submissions;
        },
        {
          wip: [],
          missed: [],
          awarded: [],
          submitted: [],
        }
      ),
    [submissionResults, timezone]
  );

  return submissions;
};
